import { create } from 'zustand'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/services/supabase/client'
import { invalidateMediaUrls } from '@/services/media/urls'
import { onAccountChange } from '@/features/offline/queue'

export interface Profile {
  id: string
  username: string
  display_name: string
  avatar_key: string | null
  bio_short: string | null
  discoverable: boolean
  searchable: boolean
  badge: string | null
  birthday: string | null
}

interface SessionStore {
  ready: boolean
  session: Session | null
  user: User | null
  profile: Profile | null
  setSession: (session: Session | null) => void
  setProfile: (profile: Profile | null) => void
  refreshProfile: () => Promise<void>
}

export const useSession = create<SessionStore>((set, get) => ({
  ready: false,
  session: null,
  user: null,
  profile: null,
  setSession: (session) => set({ session, user: session?.user ?? null, ready: true }),
  setProfile: (profile) => set({ profile }),
  refreshProfile: async () => {
    const userId = get().user?.id
    if (!userId) {
      set({ profile: null })
      return
    }
    // Right after sign-in a token can be refused for a second or two when the
    // device clock runs ahead of the server's; a couple of patient retries
    // cost nothing and keep the greeting from staying anonymous. (§110)
    for (let attempt = 0; attempt < 3; attempt++) {
      // El cumpleaños ya no se lee de la tabla: desde fuera nadie puede, ni
      // siquiera su dueño. El tuyo llega por my_birthday(). (§H4)
      const [perfil, cumple] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, username, display_name, avatar_key, bio_short, discoverable, searchable, badge')
          .eq('id', userId)
          .maybeSingle(),
        supabase.rpc('my_birthday'),
      ])
      if (!perfil.error) {
        const data = perfil.data as Omit<Profile, 'birthday'> | null
        set({
          profile: data ? { ...data, birthday: (cumple.data as string | null) ?? null } : null,
        })
        return
      }
      if (get().user?.id !== userId) return
      await new Promise((resolve) => setTimeout(resolve, 1500 * (attempt + 1)))
    }
  },
}))

/** Wires Supabase auth into the store. Called once from main.tsx. */
export function initSession() {
  supabase.auth.getSession().then(({ data }) => {
    useSession.getState().setSession(data.session)
    if (data.session) void useSession.getState().refreshProfile()
  })

  // Para no repetir la adopción de borradores en cada refresco de token.
  let cuentaVista: string | null = null

  supabase.auth.onAuthStateChange((event, session) => {
    useSession.getState().setSession(session)
    if (event === 'SIGNED_OUT') {
      useSession.getState().setProfile(null)
      // Signed URLs outlive a sign-out unless they are thrown away here. (§117)
      invalidateMediaUrls()
    } else if (session) {
      void useSession.getState().refreshProfile()
    }
    // La cola de borradores sabe de quién son: al cambiar de cuenta, se pone al
    // día; al salir, deja de enseñar y de subir los de quien se fue. (§H19)
    const ahora = session?.user.id ?? null
    if (ahora !== cuentaVista) {
      cuentaVista = ahora
      void onAccountChange(ahora)
    }
  })
}
